/**
 * What a session has in hand, folded from its own log (RFC 0297 §4a).
 *
 * DERIVED, NEVER STORED. The alternative is a set on the session with a mutable
 * `seenAt` per row, which is one record instead of a fold and needs a write on
 * every read to stay honest. That is the same write with a worse failure mode: a
 * `seenAt` that fails to update is a badge that lies, where a touch that fails to
 * append is a holding that is merely absent. Deleting every `mod.touched` entry
 * removes a session's holdings and breaks nothing else.
 *
 * THE WATERMARK IS THE LAST LOOK, and that is what makes reading the thing that
 * clears it. Every count in the bar is measured from `lastAt`, so a session that
 * re-reads a document has, by that act, caught up on it. Nothing sets a flag,
 * and there is no second path by which a badge can be wrong.
 *
 * This file knows nothing about documents, comments or staleness: it folds touches
 * and answers what each one saw. Comparing that against a document's `updatedAt`
 * needs the project, and belongs to whoever has it (§4c).
 */

import { topoOrder } from './projection-order'
import type { LogDoc, TimestampMs, TouchKind } from './types'
import { PUT_DOWN_EXTENSION, TOUCHED_EXTENSION } from './types'

/** A touch, or a putting-down, in causal order. */
type Act =
  | {
      act: 'touch'
      documentId: string
      kind: TouchKind
      sawUpdatedAt: number
      at: number
    }
  | { act: 'put_down'; documentId: string; at: number }

const TOUCH_KINDS: ReadonlySet<string> = new Set([
  'read',
  'write',
  'mention',
  'open',
])

/** Deliberate touches pin. A write decays, and a read holds nothing (§4b, §4f). */
const PINS: ReadonlySet<string> = new Set(['mention', 'open'])

/**
 * One document this session has in hand.
 *
 * `title` is NOT here. The catalog holds it, this holds the log's answer, and a
 * projection that reached for both would be unable to answer without a
 * project document it has no reason to open.
 */
export interface Holding {
  documentId: string
  /** The first touch, which is when it came into hand. */
  firstAt: TimestampMs
  /** The last time THIS session touched. Every count is measured from here. */
  lastAt: TimestampMs
  lastKind: TouchKind
  /**
   * The last touch that was a READ, or 0 for a document never read (§4c).
   *
   * A SECOND WATERMARK, because `lastAt` cannot honestly clear a comment count.
   * Every touch moves `lastAt`, and two of the kinds show nobody a comment: an
   * `open` is how you go and look at the document, and a `write` says the agent
   * touched the text. Measuring unread comments from `lastAt` therefore cleared
   * a badge for a comment nothing had handed over, which is the bar lying about
   * what the session knows, the one failure this projection exists to prevent.
   */
  lastReadAt: TimestampMs
  /** The document's `updatedAt` as of that last touch, to compare against. */
  sawUpdatedAt: TimestampMs
  /** A `mention` or an `open` has happened, so it never falls out of the list. */
  pinned: boolean
}

/** Every well-formed act on the log, in causal order. */
function acts(doc: LogDoc): Act[] {
  const out: Act[] = []
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const raw = p.payload.payload
    // Coerced with String() for the reason `projectObjects` gives: the codec
    // leaves an extension's inner payload raw, so nothing here is typed yet.
    const documentId = raw.documentId != null ? String(raw.documentId) : ''
    if (!documentId) continue
    const at = Number(raw.at ?? e.createdAt) || e.createdAt

    if (p.payload.extensionType === TOUCHED_EXTENSION) {
      const kind = raw.kind != null ? String(raw.kind) : ''
      if (!TOUCH_KINDS.has(kind)) continue
      out.push({
        act: 'touch',
        documentId,
        kind: kind as TouchKind,
        sawUpdatedAt: Number(raw.sawUpdatedAt ?? 0) || 0,
        at,
      })
      continue
    }
    if (p.payload.extensionType === PUT_DOWN_EXTENSION) {
      out.push({ act: 'put_down', documentId, at })
    }
  }
  return out
}

/**
 * The documents this session is holding, most recently touched first.
 *
 * PINNED FIRST, then recency, which is §4f's ranking and the reason `pinned`
 * exists at all. A document named in the first message must not fall off during
 * a long session, and a document read in passing must not hold its place
 * forever.
 *
 * A put-down removes it from the list and nothing else: a later touch on the same
 * document brings it back, with `firstAt` still answering when it first came
 * into hand. That is deliberate. A holding that came back reporting itself as
 * new would lose the one fact the fold exists to keep.
 */
export function projectHoldings(doc: LogDoc): Holding[] {
  const held = new Map<string, Holding>()
  const downAt = new Map<string, number>()

  for (const a of acts(doc)) {
    if (a.act === 'put_down') {
      downAt.set(a.documentId, a.at)
      continue
    }
    const prev = held.get(a.documentId)
    // A READ PUTS NOTHING IN HAND. Learned by using the bar: an agent reads
    // twenty documents to answer one question, and a bar listing all twenty
    // says nothing about what the session is working with. What puts a
    // document in hand is a person's gesture (a mention, an open) or the
    // agent changing it (a write). A read on a document already held still
    // counts, and that is the case that matters: reading is what clears a
    // CHANGED mark and hands over a comment, so it must move the watermarks.
    // A read after a put-down does not bring it back, for the same reason.
    if (a.kind === 'read') {
      const down = downAt.get(a.documentId)
      if (!prev || (down !== undefined && down >= prev.lastAt)) continue
    }
    held.set(a.documentId, {
      documentId: a.documentId,
      firstAt: prev?.firstAt ?? a.at,
      lastAt: a.at,
      lastKind: a.kind,
      // Carried forward, never reset: a write after a read does not un-read it.
      lastReadAt: a.kind === 'read' ? a.at : (prev?.lastReadAt ?? 0),
      sawUpdatedAt: a.sawUpdatedAt,
      pinned: (prev?.pinned ?? false) || PINS.has(a.kind),
    })
  }

  const out: Holding[] = []
  for (const h of held.values()) {
    // Put down, and not touched since. The comparison is `>=` on purpose: a
    // put-down is a later decision than the touch it answers, so a touch and a
    // drop in the same millisecond means dropped.
    const down = downAt.get(h.documentId)
    if (down !== undefined && down >= h.lastAt) continue
    out.push(h)
  }

  return out.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    return b.lastAt - a.lastAt
  })
}

/** A passage somebody pointed at, as a message recorded it (RFC 0297 §4e). */
export interface StampedSelection {
  documentId: string
  label: string
  ref?: string
}

/**
 * What this session was last pointed at, off its own log (RFC 0297 §4e).
 *
 * THE LAST MESSAGE THAT CARRIED ONE, not the last message. A selection is
 * sticky in the window it was made in, so a person who points once and then
 * sends three messages about it means all three. Reading only the newest
 * message would drop the passage on the second, which is exactly when they
 * stopped describing it because they had already pointed.
 *
 * Nothing clears it here. A selection is dismissed in the window that holds it,
 * and the next message it stamps replaces this one.
 */
export function projectSelection(doc: LogDoc): StampedSelection | null {
  let latest: StampedSelection | null = null
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'message') continue
    const m = p.payload as {
      selectionDocumentId?: unknown
      selectionLabel?: unknown
      selectionRef?: unknown
    }
    const documentId =
      m.selectionDocumentId != null ? String(m.selectionDocumentId) : ''
    const label = m.selectionLabel != null ? String(m.selectionLabel) : ''
    // Both, or neither. A label naming no document has no row to sit under.
    if (!documentId || !label) continue
    const ref = m.selectionRef != null ? String(m.selectionRef) : ''
    latest = { documentId, label, ...(ref ? { ref } : {}) }
  }
  return latest
}
